import type { AlertErrorHandlerFactory } from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import type { Id } from '@/infra/id'
import { type Job } from '@/features/jobs/Job'
import { type IndividualProfileStore } from '@/features/jobs/info-gathering/individual-info/IndividualProfileStore'
import { type PersonalProfileViewModel } from '@/features/jobs/info-gathering/individual-info/PersonalProfileViewModel'
import { type SpousalProfileViewModel } from '@/features/jobs/info-gathering/individual-info/SpousalProfileViewModel'
import { type DependentProfileViewModel } from '@/features/jobs/info-gathering/individual-info/DependentProfileViewModel'
import { type InfoGatheringStore } from '@/features/jobs/info-gathering/InfoGatheringStore'
import { PersonalProfileEditorViewModel } from '@/features/jobs/info-gathering/individual-info/PersonalProfileEditorViewModel'
import { SpousalProfileEditorViewModel } from '@/features/jobs/info-gathering/individual-info/SpousalProfileEditorViewModel'
import { DependentProfileEditorViewModel } from '@/features/jobs/info-gathering/individual-info/DependentProfileEditorViewModel'

/**
 * A tab of the Edit Personal Profile dialog.
 */
export type PersonalProfileTab = 'personal' | 'spousal' | 'dependents'

/**
 * The label of each tab.
 */
export const TAB_LABELS: Record<PersonalProfileTab, string> = {
  personal: 'Personal',
  spousal: 'Spouse',
  dependents: 'Dependents',
}

/**
 * View model for the dialog that edits the client's personal, spousal and
 * dependent profiles.
 */
export class EditPersonalProfileDialogViewModel {
  /**
   * The dialog's modal state.
   */
  readonly modal = new ModalViewModel()

  /**
   * Editor for the personal profile tab.
   */
  readonly personalProfileEditorVm = new PersonalProfileEditorViewModel()

  /**
   * Editor for the spousal profile tab.
   */
  readonly spousalProfileEditorVm = new SpousalProfileEditorViewModel()

  /**
   * The tab being shown.
   */
  @observable accessor activeTab: PersonalProfileTab = 'personal'

  /**
   * One editor per dependent on the dependents tab.
   */
  @observable
  accessor dependentProfileEditors: DependentProfileEditorViewModel[] = []

  /**
   * Dependent profiles removed in the dialog, delisted on the next save.
   */
  @observable accessor pendingDelistIds: Id[] = []

  constructor(
    private readonly _job: Job,
    private readonly _getPersonalProfileVm: () => PersonalProfileViewModel | null,
    private readonly _getSpousalProfileVm: () => SpousalProfileViewModel | null,
    private readonly _getDependentProfileVm: () => DependentProfileViewModel | null,
    private readonly _individualProfileStore: IndividualProfileStore,
    private readonly _infoGatheringStore: InfoGatheringStore,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * The tabs for the profiles the checklist asks for.
   */
  @computed
  get availableTabs(): PersonalProfileTab[] {
    const tabs: PersonalProfileTab[] = []
    if (this._getPersonalProfileVm()) {
      tabs.push('personal')
    }
    if (this._getSpousalProfileVm()) {
      tabs.push('spousal')
    }
    if (this._getDependentProfileVm()) {
      tabs.push('dependents')
    }
    return tabs
  }

  /**
   * Whether every profile the dialog edits has been fetched, so it can be
   * shown.
   */
  @computed
  get canShow() {
    return [
      this._getPersonalProfileVm(),
      this._getSpousalProfileVm(),
      this._getDependentProfileVm(),
    ].every((vm) => vm?.isLoaded ?? true)
  }

  /**
   * Whether the active tab is the first available one.
   */
  @computed
  get isFirstTab() {
    return this.activeTab === this.availableTabs[0]
  }

  /**
   * Whether the active tab is the last available one.
   */
  @computed
  get isLastTab() {
    return this.activeTab === this.availableTabs[this.availableTabs.length - 1]
  }

  /**
   * The tab after the active one, if any.
   */
  @computed
  get nextTab(): PersonalProfileTab | null {
    const index = this.availableTabs.indexOf(this.activeTab)
    if (index >= 0 && index < this.availableTabs.length - 1) {
      return this.availableTabs[index + 1]
    }
    return null
  }

  /**
   * The tab before the active one, if any.
   */
  @computed
  get previousTab(): PersonalProfileTab | null {
    const index = this.availableTabs.indexOf(this.activeTab)
    if (index > 0) {
      return this.availableTabs[index - 1]
    }
    return null
  }

  /**
   * Validates the active tab's editors.
   */
  @action.bound
  validate() {
    switch (this.activeTab) {
      case 'personal':
        return this.personalProfileEditorVm.validation.reset().validate()
          .isValid
      case 'spousal':
        return this.spousalProfileEditorVm.validation.reset().validate().isValid
      case 'dependents':
        return this.dependentProfileEditors
          .map((e) => e.validation.reset().validate().isValid)
          .every(Boolean)
    }
  }

  /**
   * Adds an editor for a new dependent.
   */
  @action.bound
  addDependent() {
    this.dependentProfileEditors.push(new DependentProfileEditorViewModel())
  }

  /**
   * Removes the dependent editor at the index, delisting a saved profile on the next save.
   */
  @action.bound
  removeDependentAt(index: number) {
    const editor = this.dependentProfileEditors[index]
    if (!editor) return
    if (editor.id) {
      this.pendingDelistIds.push(editor.id)
    }
    this.dependentProfileEditors.splice(index, 1)
  }

  /**
   * Switches to the tab.
   */
  @action.bound
  setTab(tab: PersonalProfileTab) {
    this.activeTab = tab
  }

  /**
   * Moves to the next tab without saving, closing the dialog after the last one.
   */
  @action.bound
  skipCurrentTab() {
    if (this.nextTab) {
      this.activeTab = this.nextTab
      return
    }

    this.modal.close()
  }

  /**
   * Moves to the previous tab.
   */
  @action.bound
  goToPreviousTab() {
    if (this.previousTab) {
      this.activeTab = this.previousTab
    }
  }

  /**
   * Shows the dialog on the personal tab.
   */
  @action.bound
  showPersonal() {
    this.show('personal')
  }

  /**
   * Shows the dialog on the spousal tab.
   */
  @action.bound
  showSpousal() {
    this.show('spousal')
  }

  /**
   * Shows the dialog on the dependents tab.
   */
  @action.bound
  showDependents() {
    this.show('dependents')
  }

  /**
   * Shows the dialog with editors filled from the saved profiles, on the tab if it is available.
   */
  @action.bound
  show(initialTab: PersonalProfileTab = 'personal') {
    this.personalProfileEditorVm.reset(this._getPersonalProfileVm()?.profile)
    this.spousalProfileEditorVm.reset(this._getSpousalProfileVm()?.profile)

    this.dependentProfileEditors = []
    this.pendingDelistIds = []
    for (const dependent of this._getDependentProfileVm()?.dependentProfiles ??
      []) {
      if (!dependent) continue
      const editor = new DependentProfileEditorViewModel()
      editor.reset(dependent)
      this.dependentProfileEditors.push(editor)
    }

    this.activeTab = this.availableTabs.includes(initialTab)
      ? initialTab
      : (this.availableTabs[0] ?? 'personal')

    this.modal.show()
  }

  /**
   * Saves the active tab and moves to the next one, closing the dialog after the last one.
   */
  submit = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    const saved = await this._saveActiveTab()
      .then(() => true)
      .catch(this._createAlertErrorHandler('Saving the profile failed'))
      .catch(() => false)
    if (!saved) {
      return
    }

    const nextTab = this.nextTab
    if (nextTab) {
      runInAction(() => {
        this.activeTab = nextTab
      })
    } else {
      this.modal.close()
    }
  })

  private async _saveActiveTab() {
    switch (this.activeTab) {
      case 'personal':
        await this._savePersonal()
        break
      case 'spousal':
        await this._saveSpousal()
        break
      case 'dependents':
        await this._saveDependents()
        break
    }
  }

  private async _savePersonal() {
    const editor = this.personalProfileEditorVm
    const saved = await this._individualProfileStore.savePersonalProfileVersion(
      {
        workspaceId: this._job.workspace.id,
        ownerUserId: this._job.championClient!.member.user.publicId,
        occupation: editor.occupation,
        personalInfo: editor.personalInfoVm.toPersonalInfo(),
        contactInfo: editor.contactInfoVm.toContactInfo(),
      },
    )

    await this._infoGatheringStore.provideSupportingItemPersonalInfo({
      jobId: this._job.id,
      itemId: this._getPersonalProfileVm()!.item.id,
      profileId: saved.id,
      profileVersion: saved.version,
    })
  }

  private async _saveSpousal() {
    const editor = this.spousalProfileEditorVm
    const saved = await this._individualProfileStore.saveSpousalProfileVersion({
      workspaceId: this._job.workspace.id,
      ownerUserId: this._job.championClient!.member.user.publicId,
      occupation: editor.occupation,
      personalInfo: editor.personalInfoVm.toPersonalInfo(),
      contactInfo: editor.toContactInfo(),
    })

    await this._infoGatheringStore.provideSupportingItemSpousalInfo({
      jobId: this._job.id,
      itemId: this._getSpousalProfileVm()!.item.id,
      profileId: saved.id,
      profileVersion: saved.version,
    })
  }

  private async _saveDependents() {
    for (const id of this.pendingDelistIds) {
      await this._infoGatheringStore.removeSupportingItemDependentInfo({
        jobId: this._job.id,
        itemId: this._getDependentProfileVm()!.item.id,
        profileId: id,
      })
    }

    for (const editor of this.dependentProfileEditors) {
      if (editor.id && !editor.isDirty) {
        continue
      }

      const personalInfo = editor.personalInfoVm.toPersonalInfo()

      const saved = editor.id
        ? await this._individualProfileStore.updateDependentProfile({
            id: editor.id,
            workspaceId: this._job.workspace.id,
            ownerUserId: this._job.championClient!.member.user.publicId,
            relationship: editor.relationship,
            personalInfo,
          })
        : await this._individualProfileStore.createDependentProfile({
            workspaceId: this._job.workspace.id,
            ownerUserId: this._job.championClient!.member.user.publicId,
            relationship: editor.relationship,
            personalInfo,
          })

      runInAction(() => {
        editor.id = saved.id
        editor.version = saved.version
      })

      await this._infoGatheringStore.provideSupportingItemDependentInfo({
        jobId: this._job.id,
        itemId: this._getDependentProfileVm()!.item.id,
        profileId: saved.id,
        profileVersion: saved.version,
      })
    }

    runInAction(() => {
      this.pendingDelistIds = []
    })
  }
}
